import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { mockData } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      toast.success("Message envoyé !", {
        description: "Nous vous contacterons dans les plus brefs délais.",
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error submitting contact form:', error);
      toast.error("Erreur", {
        description: "Une erreur est survenue. Veuillez réessayer.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Contactez-Nous
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Nous sommes à votre écoute pour répondre à toutes vos questions
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">
                  Nos Coordonnées
                </h2>
                <p className="text-gray-400 mb-8">
                  N'hésitez pas à nous contacter par téléphone, email ou via le formulaire de contact.
                </p>
              </div>

              <div className="space-y-6">
                <Card className="bg-slate-900 border-slate-800 hover:border-amber-600/50 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-amber-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">Téléphone</h3>
                        <a href={`tel:${mockData.company.phone}`} className="text-gray-400 hover:text-amber-500 transition-colors">
                          {mockData.company.phone}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900 border-slate-800 hover:border-amber-600/50 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-amber-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">Email</h3>
                        <a href={`mailto:${mockData.company.email}`} className="text-gray-400 hover:text-amber-500 transition-colors">
                          {mockData.company.email}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900 border-slate-800 hover:border-amber-600/50 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-amber-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MessageSquare className="w-6 h-6 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">WhatsApp</h3>
                        <a href={`https://wa.me/${mockData.company.whatsapp.replace(/\s/g, '')}`} className="text-gray-400 hover:text-amber-500 transition-colors">
                          {mockData.company.whatsapp}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-amber-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">Zone de Service</h3>
                        <p className="text-gray-400">Nice et toute la Côte d'Azur</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900 border-slate-800">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-amber-600/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">Disponibilité</h3>
                        <p className="text-gray-400">{mockData.company.availability}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-white mb-6">
                    Demande de Devis
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-white">Nom complet *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="bg-slate-950 border-slate-700 text-white focus:border-amber-500"
                          placeholder="Jean Dupont"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-white">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="bg-slate-950 border-slate-700 text-white focus:border-amber-500"
                          placeholder="jean@exemple.fr"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-white">Téléphone *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="bg-slate-950 border-slate-700 text-white focus:border-amber-500"
                          placeholder="+33 6 XX XX XX XX"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="service" className="text-white">Service souhaité</Label>
                        <select
                          id="service"
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          className="w-full bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 focus:border-amber-500 focus:outline-none"
                        >
                          <option value="">Sélectionnez un service</option>
                          <option value="airport">Transfert Aéroport</option>
                          <option value="business">Transport Business</option>
                          <option value="disposal">Mise à Disposition</option>
                          <option value="tourism">Tourisme & Événements</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-white">Votre message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="bg-slate-950 border-slate-700 text-white focus:border-amber-500 resize-none"
                        placeholder="Décrivez votre besoin : trajet, date, nombre de passagers..."
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg transition-all duration-300 hover:shadow-lg hover:shadow-amber-600/50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      {isSubmitting ? 'Envoi en cours...' : 'Envoyer la Demande'}
                    </Button>

                    <p className="text-gray-400 text-sm text-center">
                      Nous vous répondrons dans les 24 heures
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Besoin d'une Réponse Immédiate ?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Appelez-nous directement, nous sommes disponibles 24h/24 et 7j/7
          </p>
          <a href={`tel:${mockData.company.phone}`}>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6 transition-all duration-300 hover:shadow-lg hover:shadow-amber-600/50">
              <Phone className="w-5 h-5 mr-2" />
              {mockData.company.phone}
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
